import { APPLE_APP_STORE_BASE_URL, GOOGLE_PLAY_STORE_URL } from '@constants/mobile-app-store';
import { AppStoreType, MobileRedirectParams } from 'types/mobile-redirects';
import { FundriseRouteConfig, RouteLocationNormalized } from 'vue-router';
import { isAndroidDevice, isIphone } from '@utils/client';
import { appStoreVisitRedirect } from '@api/mobile-redirect';
import { flattenQueryParam } from '@utils/request';
import { logError } from '@utils/error-tracking';

/**
 * Constructs the appropriate app store URL based on type and UTM parameters
 * Pulls from api-services AppVisitController.java to build url
 */
const getAppStoreUrl = (appType: AppStoreType, utmSource?: string | null): string => {
	if (appType === 'GOOGLE_PLAY') {
		return GOOGLE_PLAY_STORE_URL;
	} else if (appType === 'APPLE_APP_STORE') {
		// Apple requires utm_source in the ct parameter
		const campaign = utmSource ? `?ct=${encodeURIComponent(utmSource)}&mt=8` : '?mt=8';
		return `${APPLE_APP_STORE_BASE_URL}${campaign}`;
	}
	const error = new Error(`Unknown appType passed to getAppStoreUrl: ${appType}`);
	logError(error, 'error', { key: 'getAppStoreUrl', data: { appType } });
	throw error;
};

/**
 * Tracks the app store visit using sendBeacon for reliable delivery during page unload.
 * Falls back to fire-and-forget fetch if sendBeacon is unavailable.
 */
const trackAppStoreVisit = (to: RouteLocationNormalized, appType: AppStoreType): void => {
	const params: MobileRedirectParams = {
		utmCampaign: flattenQueryParam(to.query.utm_campaign) ?? null,
		utmContent: flattenQueryParam(to.query.utm_content) ?? null,
		utmMedium: flattenQueryParam(to.query.utm_medium) ?? null,
		utmSource: flattenQueryParam(to.query.utm_source) ?? null,
		distinctId: flattenQueryParam(to.query.distinct_id) ?? null,
		clickId: flattenQueryParam(to.query.clickid) ?? null,
		appType
	};

	const apiUrl = `${import.meta.env.VITE_BASE_API_URL}/api/store/visit`;

	if (navigator.sendBeacon) {
		const blob = new Blob([JSON.stringify(params)], { type: 'application/json' });
		const success = navigator.sendBeacon(apiUrl, blob);
		if (!success) {
			logError(new Error('sendBeacon returned false'), 'warning', {
				key: 'trackAppStoreVisit',
				data: { appType }
			});

			appStoreVisitRedirect(params, { suppressToast: () => true }).catch((err) => {
				logError(new Error('Failed to track app store visit', { cause: err }), 'error', {
					key: 'appStoreVisitRedirect',
					data: { appType, originalError: err }
				});
			});
		}
		return;
	}
	// Fallback for browsers without sendBeacon support
	appStoreVisitRedirect(params, { suppressToast: () => true }).catch((err) => {
		logError(new Error('Failed to track app store visit', { cause: err }), 'error', {
			key: 'appStoreVisitRedirect',
			data: { appType, originalError: err }
		});
	});
};

/**
 * Handles mobile app store redirect logic - redirects immediately and tracks asynchronously
 */
const handleAppStoreRedirect = (to: RouteLocationNormalized, appType: AppStoreType): void => {
	const utmSource = flattenQueryParam(to.query.utm_source) ?? null;
	const redirectUrl = getAppStoreUrl(appType, utmSource);

	// Track the visit asynchronously (fire-and-forget)
	trackAppStoreVisit(to, appType);

	// Redirect immediately - don't wait for analytics
	window.location.href = redirectUrl;
};

const mobileRedirectRoutes: Array<FundriseRouteConfig> = [
	{
		path: '/play-store-redirect',
		name: 'play-store-redirect',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: (to) => {
			handleAppStoreRedirect(to, 'GOOGLE_PLAY');
		},
		component: () => import('@views/marketing-site/home-view.vue')
	},
	{
		path: '/app-store-redirect',
		name: 'app-store-redirect',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: (to) => {
			handleAppStoreRedirect(to, 'APPLE_APP_STORE');
		},
		component: () => import('@views/marketing-site/home-view.vue')
	},
	{
		path: '/invest-now',
		name: 'invest-now',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: (to) => {
			if (isIphone()) {
				return { name: 'app-store-redirect', query: to.query };
			} else if (isAndroidDevice()) {
				return { name: 'play-store-redirect', query: to.query };
			} else {
				return { name: 'start-investing', query: to.query };
			}
		}
	}
];

export default mobileRedirectRoutes;
